import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Modal,
  Stack,
  Loader,
  Text,
  Group,
  ActionIcon,
  Checkbox,
  Center,
} from "@mantine/core";
import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";
import { TextWithInfo } from "./textWithInfo";
import { DataTable } from "mantine-datatable";
import {
  convertSatsToFiatStringRoundedToNearestFifty,
  convertSatsToFiatStringRoundedToNearestWhole,
} from "../../helpers/conversions";
import { IconPlayerPlayFilled, IconRocket } from "@tabler/icons-react";

import InboundInvoiceQR from "../interactiveData/inboundInvoiceQR";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";

const GetInbound = observer(({ cubby }: { cubby: Cubby }) => {
  const [isPrivate, setIsPrivate] = useState(true);

  useEffect(() => {
    cubby.setChannelOffers(null);
    cubby.setChannelOfferPendingAnInvoiceUuid(null);
    cubby.setChannelOfferInvoiceForUserPayment(null);
    cubby.cubbyFastInfo("get_channel_offers");
  }, []);

  const records = cubby.channelOffers?.data?.amounts
    ? Object.entries(cubby.channelOffers.data.amounts).map(
        ([amount, fee], index) => ({
          id: index + 1,
          amount: amount,
          fee: fee,
          button: "foo",
        })
      )
    : [];

  const SatsRenderer = ({ value, convertToFiat, cubby, compact = false }) => {
    const fiatValue = convertToFiat(value, cubby);

    return (
      <Stack gap="xs" style={{ minWidth: compact ? "auto" : "120px" }}>
        <Group gap="xs" justify="flex-start">
          <Text size="sm">{Number(value).toLocaleString()}</Text>
          <Text ml="-.25rem" size="xs">
            sats
          </Text>
        </Group>
        <Text c="dimmed" mt={compact ? 0 : "-.7rem"} size="xs">
          {fiatValue}
        </Text>
      </Stack>
    );
  };

  const ChannelSizeRenderer = ({ value }) => (
    <SatsRenderer
      value={value}
      convertToFiat={convertSatsToFiatStringRoundedToNearestFifty}
      cubby={cubby}
    />
  );

  const ChannelFeeRenderer = ({ value }) => (
    <SatsRenderer
      value={value}
      convertToFiat={convertSatsToFiatStringRoundedToNearestWhole}
      cubby={cubby}
    />
  );

  const ButtonRenderer = ({ row }) => (
    <ActionIcon
      size="sm"
      variant="subtle"
      onClick={(e: React.MouseEvent) => {
        console.log("clicked on row", row);
      }}
    >
      <IconRocket size={16} />
    </ActionIcon>
  );

  return (
    <Modal
      opened={cubby.currentModal == MODALS.GET_INBOUND}
      onClose={() => {
        cubby.setModal(null);
        cubby.setChannelOffers(null);
        cubby.setChannelOfferPendingAnInvoiceUuid(null);
        cubby.setChannelOfferInvoiceForUserPayment(null);
      }}
      title={
        <TextWithInfoAndIcon
          text="Get Inbound"
          articleSlug={ARTICLES.GET_INBOUND.slug}
          icon={<IconRocket size={24} />}
          cubby={cubby}
          textSize="lg"
        />
      }
    >
      <Stack>
        {!cubby.channelOffers?.data &&
          !cubby.channelOfferInvoiceForUserPayment && (
            <Center maw={400} h={100}>
              <Loader size="xl" />
            </Center>
          )}
        {cubby.channelOfferInvoiceForUserPayment && (
          <>
            <InboundInvoiceQR cubby={cubby} />
          </>
        )}
        {cubby.channelOffers?.data && (
          <>
            <DataTable
              withTableBorder
              borderRadius="sm"
              withColumnBorders
              striped
              highlightOnHover
              records={records}
              columns={[
                {
                  accessor: "amount",
                  title: "Channel Capacity",
                  render: (row) => <ChannelSizeRenderer value={row.amount} />,
                },
                {
                  accessor: "fee",
                  title: "Cost",
                  render: (row) => <ChannelFeeRenderer value={row.fee} />,
                },
                // {
                //   accessor: "button",
                //   title: "",
                //   render: (row) => <ButtonRenderer row={row} />,
                // },
              ]}
              onRowClick={({ record }) =>
                cubby.handleChannelOfferSelection(
                  Number(record.amount),
                  isPrivate
                )
              }
            />
            <Text c="dimmed" ta="center" size="sm">
              Choose a channel capacity to get inbound.
            </Text>
            <Group justify="center" mt="md">
              <Checkbox
                label="Private Channel"
                checked={isPrivate}
                onChange={(event) => setIsPrivate(event.currentTarget.checked)}
              />
            </Group>
          </>
        )}
        {cubby.channelOffers?.data?.error && (
          <Text c="red">{cubby.channelOffers.data.error}</Text>
        )}

        {/* <Text>demo below</Text> */}
      </Stack>
    </Modal>
  );
});

export default GetInbound;
